import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import type { File } from '@ztechapps/common.contract'
import type { AiChatProviderProps } from './Context'
import { useAiChatContext, AiChatProvider } from './Context'
import { jsonTryParse } from '@ztechapps/common.utils'
import { Stack } from 'react-bootstrap'
import { ChatInput } from '@ztechapps/webui.input'
import { Scrollable, Alert, ShowIf } from '@ztechapps/webui.util'
import { useMeasure } from 'react-use'
import { MessageList } from './MessageList'
import { LoadingIndicator } from './LoadingIndicator'
import { AttachFileButton, type SupportedMimeType } from './AttachFileButton'
import { FileAttachmentPreview } from './FileAttachmentPreview'
import { SessionDropdown } from './SessionDropdown'
import { useFloatingChatBubbleContext } from '../floating-chat-bubble/Context'
import { parseRateLimitDenial, formatRetryAfter } from './rateLimitDenial'
import { isCreditsExhausted } from './isCreditsExhausted'

export type AiChatProps = Omit<AiChatProviderProps, 'children'>

/**
 * AiChat component provides a complete chat interface for AI interactions.
 * Uses Vercel AI SDK's useChat hook for streaming responses and tool calling.
 *
 * @example
 * ```tsx
 * <AiChat height={500} />
 * ```
 */
export const AiChat = (props: AiChatProps) => {
  return (
    <AiChatProvider {...props}>
      <Layout />
    </AiChatProvider>
  )
}

/**
 * Internal Layout component that consumes the AiChat context
 */
const Layout = () => {
  const {
    messages,
    input,
    setInput,
    handleSubmit,
    sendMessage,
    isLoading,
    error,
    height = 400,
    attachedFile,
    setAttachedFile,
    setAttachmentUrl,
    attachmentMimetype,
    setAttachmentMimetype,
    autoSendOnOpen,
    newSessionOnAutoSend,
    helpAction,
    creditsAction,
    contextLabel,
    createNewSession,
    currentSession,
  } = useAiChatContext()

  // Detect rateLimit.denied payload from the backend (429 body) and swap
  // the generic error message for the rate-limit message. The Vercel AI
  // SDK surfaces the 429 response body as `error.message`, so we parse it
  // here and fall back to the raw text when the shape doesn't match.
  const rateLimitDenial = useMemo(() => parseRateLimitDenial(error), [error])

  // Same idea for the 422 `credits.exhausted` body: the notice replaces the
  // raw JSON message and offers the caller's `creditsAction` beside it.
  const creditsExhausted = useMemo(() => isCreditsExhausted(error), [error])

  // A server error arrives as a JSON body in `error.message` (e.g. `{"error":"Internal Server Error"}`);
  // show its `message`/`error` text rather than the raw JSON.
  const errorText = useMemo(() => {
    const body = jsonTryParse(error?.message)
    const text = body?.message ?? body?.error
    return typeof text === 'string' ? text : error?.message
  }, [error])

  // When used inside AiChatBubble, this gives us access to open/close the
  // floating modal. Outside of AiChatBubble the context returns an empty
  // object, so `open` is undefined and the auto-send effect becomes a no-op
  // for the opening side — sendMessage still fires.
  const { open: openFloatingBubble, isMaximized } = useFloatingChatBubbleContext()

  // Track the last value we auto-sent so we don't re-send on every re-render
  // when `autoSendOnOpen` stays the same. Bumping the value (including
  // clearing it and re-setting) re-arms the bridge.
  const lastAutoSentRef = useRef<string | undefined>(undefined)

  // When `newSessionOnAutoSend` is true, the auto-send effect must first
  // create a fresh session and WAIT for the session switch (which empties
  // the messages array via the `currentSession?._id` effect in Context.tsx)
  // before calling `sendMessage`. We stage the pending value here and let a
  // second effect fire the send once the session id has changed and the
  // messages list is empty. This matches the "every click starts a clean
  // conversation" requirement from the storefront Search via AI spec.
  const pendingAutoSendRef = useRef<string | undefined>(undefined)
  const [pendingSessionId, setPendingSessionId] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (isEmpty(autoSendOnOpen)) {
      // Clear the dedup ref when the value is empty/undefined so the next
      // non-empty value is always treated as a new auto-send. This is what
      // StoreAiChatOpenContext.open()'s two-phase setState dance relies on to
      // re-arm the bridge when the user re-submits the same keyword.
      lastAutoSentRef.current = undefined
      return
    }
    if (lastAutoSentRef.current === autoSendOnOpen) {
      return
    }
    lastAutoSentRef.current = autoSendOnOpen
    openFloatingBubble?.()

    if (newSessionOnAutoSend) {
      // Stage the value and create a new session. The second effect below
      // sends once the session switch has propagated.
      pendingAutoSendRef.current = autoSendOnOpen
      setPendingSessionId(currentSession?._id)
      createNewSession()
      return
    }

    sendMessage(autoSendOnOpen as string)
  }, [autoSendOnOpen, openFloatingBubble, sendMessage, newSessionOnAutoSend, createNewSession, currentSession?._id])

  // Second stage of the auto-send-with-new-session flow. Fires the pending
  // message exactly when the session id flips away from the one we captured
  // at stage 1 — at that point the Context.tsx `currentSession?._id` effect
  // has already called `setMessages([])` and `useChat`'s state is a clean
  // slate for the new turn.
  useEffect(() => {
    const pending = pendingAutoSendRef.current
    if (!pending) return
    if (currentSession?._id === pendingSessionId) return
    pendingAutoSendRef.current = undefined
    sendMessage(pending)
  }, [currentSession?._id, pendingSessionId, sendMessage])

  // A new empty session opens at the top. The old scroll offset survives the session switch, and the
  // bottom scroll in MessageList only runs for messages — so reset it once the messages have cleared.
  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (isEmpty(messages)) scrollRef.current?.scrollTo({ top: 0 })
  }, [messages])

  // Measure input area and header to calculate available space for messages
  const [inputHeightRef, { height: inputHeight }] = useMeasure<HTMLDivElement>()
  const [headerHeightRef, { height: headerHeight }] = useMeasure<HTMLDivElement>()

  // Handle send from ChatInput
  const onSend = () => {
    if (input.trim()) {
      // Create a synthetic form event to trigger submit
      handleSubmit()
    }
  }

  // Handle file attachment (PDF or image)
  const handleFileAttached = useCallback(
    (file: File, mimeType: SupportedMimeType) => {
      setAttachedFile(file)
      setAttachmentUrl(file.uri)
      setAttachmentMimetype(mimeType)
    },
    [setAttachedFile, setAttachmentUrl, setAttachmentMimetype]
  )

  // Handle file removal
  const handleRemoveAttachment = useCallback(() => {
    setAttachedFile(undefined)
    setAttachmentUrl(undefined)
    setAttachmentMimetype(undefined)
  }, [setAttachedFile, setAttachmentUrl, setAttachmentMimetype])

  // Custom attach button that uses AttachFileButton (supports PDFs and images)
  const AttachButton = useCallback(
    () => <AttachFileButton onFileAttached={handleFileAttached} disabled={isLoading} />,
    [handleFileAttached, isLoading]
  )

  // Calculate available height for message area
  const messageAreaHeight = height - inputHeight - headerHeight - 60 // 60px for padding/margins

  return (
    <Stack gap={2} className="h-100">
      {/* Header with session dropdown */}
      <div ref={headerHeightRef}>
        <SessionDropdown />
        {contextLabel}
      </div>

      {/* Error display — rate-limit denial and exhausted credits get their own copy; all other errors fall through to the generic alert. */}
      {error && (
        <>
          <Alert
            type={rateLimitDenial || creditsExhausted ? 'warning' : 'danger'}
            className="mb-0"
            icon={rateLimitDenial ? 'hourglass-half' : creditsExhausted ? 'coins' : 'exclamation-triangle'}
            text={
              rateLimitDenial ? (
                `You've reached the AI query limit. Please try again in ${formatRetryAfter(rateLimitDenial.retryAfterSeconds)}.`
              ) : creditsExhausted ? (
                <>AI credits are used up. Contact your administrator to top up. {creditsAction}</>
              ) : (
                errorText || 'An error occurred while communicating with the AI.'
              )
            }
          />
          <ShowIf condition={!!helpAction}>
            <div className="mt-2">{helpAction}</div>
          </ShowIf>
        </>
      )}

      {/* Message area with scrollable container. Maximized, it fills the space down to the composer;
          otherwise it is capped at the computed height. */}
      <Scrollable
        ref={scrollRef}
        className={isMaximized ? 'flex-grow-1' : undefined}
        height={isMaximized ? 'none' : messageAreaHeight}
        showThumb={true}
      >
        <MessageList messages={messages} />
        <LoadingIndicator isLoading={isLoading} />
      </Scrollable>

      {/* Input area pinned to the bottom — `mt-auto` in the flex-column h-100
          parent absorbs any free space below the message Scrollable so the
          composer always sits at the bottom of the modal, both at default
          size and when the user maximizes to 92vh. */}
      <div ref={inputHeightRef} className="mt-auto">
        <Stack gap={2}>
          <div className="text-secondary small">
            <span className="text-warning-emphasis">AI can make mistakes. Check important info.</span>
          </div>

          {/* File Attachment Preview (PDF or image) */}
          {attachedFile && (
            <FileAttachmentPreview
              file={attachedFile}
              mimeType={attachmentMimetype}
              onRemove={handleRemoveAttachment}
              disabled={isLoading}
            />
          )}

          <ChatInput
            message={input}
            setMessage={setInput}
            onSend={onSend}
            isBusy={isLoading}
            shouldClearAfterSend={true}
            placeholder={attachedFile ? 'Describe what you want to do with this document...' : 'Ask anything...'}
            components={{ AttachButton }}
          />
        </Stack>
      </div>
    </Stack>
  )
}
